import React from "react";
import { useForm } from "react-hook-form";
import { omit, orderBy, uniq } from "lodash";
import { Prompt, useHistory } from "react-router";

import useViewer from "../hooks/useViewer";

import useTranslations from "../hooks/useTranslations";
import useNotification from "../hooks/useNotification";

import TemplateForm from "../components/templates/TemplateForm";

import { Container } from "../components/wombat-ui";
import {
  InsertTemplateInput,
  useInsertTemplate,
} from "../graphql/generated/definitions";

export const DEFAULT_TEMPLATE = {
  id: null,
  title: "",
  sections: [{ questions: [] }],
};

const TemplateFormScreen = ({
  template: { sections, ...baseTemplate } = DEFAULT_TEMPLATE,
  returnUrl = "/templates",
}) => {
  const {
    register,
    unregister,
    setValue,
    getValues,
    handleSubmit,
    trigger,
    control,
    errors,
    formState,
    reset,
  } = useForm<{ templateAttributes: InsertTemplateInput }>({
    shouldUnregister: true,
    mode: "onBlur",
    defaultValues: {
      templateAttributes: {
        ...baseTemplate,
        sectionsAttributes: orderBy(
          sections.map(({ questions, ...section }) => ({
            ...section,
            questionsAttributes: orderBy(questions, "order", "asc").map(
              ({ documents, ...question }) => ({
                ...question,
                documents,
                documentsBlobIds: documents.map(({ blodId }) => blodId),
              })
            ),
          })),
          "order",
          "asc"
        ),
      },
    },
  });

  const t = useTranslations();
  const history = useHistory();
  const [insertTemplate] = useInsertTemplate();
  const { notifySuccess, notifyError } = useNotification();
  const viewer = useViewer();

  const onSubmit = handleSubmit(({ templateAttributes }) => {
    insertTemplate({
      variables: {
        input: {
          id: baseTemplate.id,
          companyId: viewer.company.id,
          title: templateAttributes.title,
          sectionsAttributes: (templateAttributes.sectionsAttributes || []).map(
            (section, index) => {
              return {
                ...section,
                order: index,
                questionsAttributes: (section.questionsAttributes || []).map(
                  (questionAttributes, index) => {
                    return {
                      ...omit(
                        questionAttributes,
                        "skill",
                        "defaultValueSchema",
                        "digest",
                        "documents"
                      ),
                      order: index,
                      expectedTimeUnit: "hours",
                      documentsBlobIds: uniq(
                        (questionAttributes.documents || []).map(
                          ({ blobId }) => blobId
                        )
                      ),
                    };
                  }
                ),
              };
            }
          ),
        },
      },
    })
      .then(() => {
        notifySuccess({
          title: t("templates.notification.save.success") as string,
        });

        history.push(returnUrl);
      })
      .catch(() => {
        notifyError({
          title: t("templates.notification.save.error") as string,
        });
      });
  });

  return (
    <Container className={`flex-grow w-full`}>
      <Prompt
        when={
          Object.keys(formState.dirtyFields).length > 0 &&
          !formState.isSubmitted
        }
        message={t("forms.warning.unsavedChangesLeaveWarning") as string}
      />
      <form
        id="templateForm"
        className="flex flex-col space-y-4"
        onSubmit={onSubmit}
      >
        <TemplateForm
          trigger={trigger}
          register={register}
          unregister={unregister}
          setValue={setValue}
          getValues={getValues}
          control={control}
          errors={errors?.templateAttributes}
          reset={reset}
          name="templateAttributes"
        />
      </form>
    </Container>
  );
};

export default React.memo(TemplateFormScreen);
